// ===========================================
// STATS PANEL - Statistics Display with Collapsible Sections
// ===========================================

import { useState, useEffect, useRef } from 'react';
import type {
  StatsData,
  TableStats,
  PlayerStats,
  GlobalStats,
  SidebetStats,
} from '@busted-blackjack/shared';
import { useCardsDealt, usePenetration, useReshufflePending, useGameStateStore } from '../../stores/gameStateStore';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../api';

// Re-export for convenience
export type { StatsData } from '@busted-blackjack/shared';

interface StatsPanelProps {
  stats: StatsData | null;
  /** Font size class to apply (e.g., 'text-xs', 'text-sm', 'text-base') */
  fontClass?: string;
}

// ===========================================
// HELPER COMPONENTS
// ===========================================

interface CollapsibleSectionProps {
  title: string;
  defaultOpen?: boolean;
  titleClassName?: string;
  children: React.ReactNode;
}

function CollapsibleSection({ title, defaultOpen = false, titleClassName, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 flex items-center justify-between bg-black/20 hover:bg-black/30 transition-colors"
      >
        <span className={`font-semibold tracking-wide uppercase ${titleClassName || 'text-white/90'}`}>{title}</span>
        <span className="text-white/60">{isOpen ? '▼' : '▶'}</span>
      </button>
      {isOpen && (
        <div className="px-3 py-2 space-y-1 bg-black/10">
          {children}
        </div>
      )}
    </div>
  );
}

interface StatRowProps {
  label: string;
  value: string | number;
  highlight?: boolean;
}

function StatRow({ label, value, highlight = false }: StatRowProps) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-white/60">{label}</span>
      <span className={highlight ? 'text-yellow-400 font-medium' : 'text-white/90'}>
        {value}
      </span>
    </div>
  );
}

// ===========================================
// STAT INFO MODAL
// ===========================================

interface StatInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  formula?: string;
  theMath?: string;
  expected?: string;
  expectedLabel?: string;  // Custom label (default: "Expected")
  expectedSubLabel?: string; // Subtitle under expected value (default: "(long-term)")
  actual?: string;
  actualLabel?: string;    // Custom label (default: "Actual")
  actualSubLabel?: string; // Subtitle under actual value (default: "(this session)" or sample size)
  actualColorClass?: string; // Custom color class for actual value (default: "text-yellow-400")
  sampleSize?: number;
  minSampleSize?: number;
  lowerIsBetter?: boolean; // If true, show indicator that lower values are better
  sectionTitle?: string;   // Optional title for the Expected vs Actual section
}

function StatInfoModal({
  isOpen,
  onClose,
  title,
  description,
  formula,
  theMath,
  expected,
  expectedLabel = 'Expected',
  expectedSubLabel,
  actual,
  actualLabel = 'Actual',
  actualSubLabel,
  actualColorClass = 'text-yellow-400',
  sampleSize,
  minSampleSize = 100,
  lowerIsBetter = false,
  sectionTitle,
}: StatInfoModalProps) {
  if (!isOpen) return null;

  const isSmallSample = sampleSize !== undefined && sampleSize < minSampleSize;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border border-white/20 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Description */}
          <p className="text-white/80 text-sm leading-relaxed">{description}</p>

          {/* Formula */}
          {formula && (
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wide mb-1">Formula</div>
              <div className="bg-black/30 rounded p-2 font-mono text-sm text-green-400">
                {formula}
              </div>
            </div>
          )}

          {/* The Math */}
          {theMath && (
            <div>
              <div className="text-xs text-white/40 uppercase tracking-wide mb-1">The Math</div>
              <p className="text-white/70 text-sm leading-relaxed">{theMath}</p>
            </div>
          )}

          {/* Expected vs Actual */}
          {(expected || actual) && (
            <div className="bg-white/5 rounded-lg p-3">
              {sectionTitle && (
                <div className="text-xs text-white/60 uppercase tracking-wide text-center mb-2 font-semibold">{sectionTitle}</div>
              )}
              {lowerIsBetter && (
                <div className="text-xs text-green-400 text-center mb-2">↓ Lower is better for you</div>
              )}
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wide mb-1">{expectedLabel}</div>
                  <div className="text-lg font-semibold text-blue-400">{expected || '—'}</div>
                  <div className="text-xs text-white/40">{expectedSubLabel ?? '(long-term)'}</div>
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wide mb-1">{actualLabel}</div>
                  <div className={`text-lg font-semibold ${actualColorClass}`}>{actual || '—'}</div>
                  <div className="text-xs text-white/40">{actualSubLabel ?? (sampleSize ? `(${sampleSize} hands)` : '(this session)')}</div>
                </div>
              </div>
            </div>
          )}

          {/* Sample Size Warning */}
          {isSmallSample && (
            <div className="flex items-start gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded p-3">
              <span className="text-yellow-400">⚠</span>
              <p className="text-yellow-200/80 text-xs leading-relaxed">
                Small sample size. Results converge to expected values over {minSampleSize}+ hands.
                Short-term variance is normal.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================
// STAT ROW WITH INFO MODAL
// ===========================================

interface StatInfo {
  title: string;
  description: string;
  formula?: string;
  theMath?: string;
  expected?: string;
  expectedLabel?: string;
  expectedSubLabel?: string;
  actualLabel?: string;
  actualSubLabel?: string;
  minSampleSize?: number;
  lowerIsBetter?: boolean;
  sectionTitle?: string;
}

interface StatRowWithInfoProps {
  label: string;
  value: string | number;
  highlight?: boolean;
  info: StatInfo;
  actual?: string;
  actualColorClass?: string;
  sampleSize?: number;
}

function StatRowWithInfo({ label, value, highlight = false, info, actual, actualColorClass, sampleSize }: StatRowWithInfoProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className="flex justify-between items-center">
        <button
          onClick={() => setIsModalOpen(true)}
          className="text-white/60 hover:text-white/90 hover:underline decoration-dotted underline-offset-2 transition-colors text-left"
        >
          {label} <span className="text-white/30 opacity-80">ⓘ</span>
        </button>
        <span className={highlight ? 'text-yellow-400 font-medium' : 'text-white/90'}>
          {value}
        </span>
      </div>

      <StatInfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={info.title}
        description={info.description}
        formula={info.formula}
        theMath={info.theMath}
        expected={info.expected}
        expectedLabel={info.expectedLabel}
        expectedSubLabel={info.expectedSubLabel}
        actual={actual}
        actualLabel={info.actualLabel}
        actualSubLabel={info.actualSubLabel}
        actualColorClass={actualColorClass}
        sampleSize={sampleSize}
        minSampleSize={info.minSampleSize}
        lowerIsBetter={info.lowerIsBetter}
        sectionTitle={info.sectionTitle}
      />
    </>
  );
}

// ===========================================
// FORMATTING HELPERS
// ===========================================

function formatNumber(num: number, precision?: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(precision ?? 1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(precision ?? 1) + 'K';
  }
  return num.toLocaleString('en-US');
}

function _formatChips(num: number, precision?: number): string {
  const prefix = num >= 0 ? '' : '-';
  const absNum = Math.abs(num);
  return prefix + formatNumber(absNum, precision);
}

function formatBucks(num: number): string {
  const prefix = num >= 0 ? '' : '-';
  return prefix + Math.abs(num).toLocaleString('en-US');
}

function formatPercent(num: number): string {
  return num.toFixed(1) + '%';
}

// ===========================================
// SECTION RENDERERS
// ===========================================

// Penetration stat info (defined here since it needs access to hooks)
const PENETRATION_INFO: StatInfo = {
  title: 'Deck Penetration',
  description: 'Progress toward the reshuffle point. At 100%, the shoe will be reshuffled after the current hand completes.',
  formula: 'Cards Dealt ÷ Reshuffle Threshold × 100',
  theMath: 'Our 6-deck shoe (312 cards) reshuffles at 100% (260 cards dealt, ~1 deck remaining). Card counting requires enough cards dealt for the true count to stabilize. Below 50%, count-based decisions are unreliable—BUSTED! Buddy gives only basic strategy advice. From 50-100%, deviation advice (index plays) becomes meaningful. This 50% counting window is where following the count provides its edge.',
  sectionTitle: 'DEVIATION ADVICE',
  expected: '50-100%',
  expectedLabel: 'During',
  expectedSubLabel: '(count window)',
  actualLabel: 'Current',
  actualSubLabel: '(this session)',
  minSampleSize: 1,
};

function PenetrationDisplay() {
  const cardsDealt = useCardsDealt();
  const penetration = usePenetration();
  const reshufflePending = useReshufflePending();

  const displayValue = reshufflePending ? 'RESHUFFLE DUE' : `${cardsDealt}/${penetration.toFixed(1)}%`;

  // Color coding: red below 50% (no deviations), green at 50%+ (counting window)
  const actualColorClass = penetration >= 50 ? 'text-green-400' : 'text-red-400';

  return (
    <StatRowWithInfo
      label="Deck Penetration"
      value={displayValue}
      highlight={reshufflePending}
      info={PENETRATION_INFO}
      actual={penetration.toFixed(1) + '%'}
      actualColorClass={actualColorClass}
    />
  );
}

// Stat info definitions for Table stats
const TABLE_STAT_INFO = {
  handsPlayed: {
    title: 'Hands Played',
    description: 'Total number of hands dealt at this table since it was created. Each player hand counts separately (splits count as multiple hands).',
    theMath: 'A typical blackjack table deals 60-80 hands per hour with a full table, or up to 200+ hands per hour heads-up against the dealer.',
  },
  uniquePlayers: {
    title: 'Unique Players',
    description: 'Number of distinct players who have played at this table. Players are counted once regardless of how many hands they play.',
  },
  dealerWinRate: {
    title: 'Dealer Win Rate',
    description: 'Percentage of hands won by the dealer at this table. Does not include pushes (ties).',
    formula: 'Dealer Wins ÷ (Total Hands − Pushes) × 100',
    theMath: 'Expected dealer win rate is approximately 48% (player wins ~42%, pushes ~8-10%). Higher dealer win rates indicate normal variance or poor player strategy at the table.',
    expected: '~48%',
    minSampleSize: 100,
  },
  blackjacksDealt: {
    title: 'Blackjacks Dealt',
    description: 'Total natural 21s (Ace + 10-value card on initial deal) dealt to players at this table. Blackjack typically pays 3:2.',
    theMath: 'Probability of blackjack is 4.83% (about 1 in 21 hands). Blackjacks are the most valuable hands, paying 150% of the original bet.',
    expected: '~4.8%',
    minSampleSize: 100,
  },
  biggestWin: {
    title: 'Biggest Win',
    description: 'Largest single-hand win at this table. Includes doubled and split hands.',
    theMath: 'Big wins typically come from doubled blackjacks, successful double downs on 11 vs dealer bust, or winning multiple split hands.',
  },
  hotStreak: {
    title: 'Hot Streak',
    description: 'Longest consecutive winning streak at this table. Pushes do not break the streak.',
    theMath: 'With a ~42% win rate, expected longest streak in 100 hands is about 5-6 wins. Streaks of 8+ are uncommon but occur naturally.',
    expected: '5-6 wins',
    minSampleSize: 100,
  },
  totalWagered: {
    title: 'Total Wagered',
    description: 'Sum of all bets placed at this table, including initial bets, doubles, splits, and insurance.',
    theMath: 'Also known as "handle" in casino terminology. The house edge applies to this total amount.',
  },
  totalPayouts: {
    title: 'Total Payouts',
    description: 'Sum of all winnings paid out to players at this table, including blackjack payouts (3:2) and insurance payouts (2:1).',
    formula: 'House Edge ≈ (Total Wagered − Total Payouts) ÷ Total Wagered × 100',
    theMath: 'The difference between Total Wagered and Total Payouts represents the theoretical house profit. With perfect basic strategy, expect payouts of ~99.5% of wagered amount long-term.',
    expected: '~99.5%',
    expectedLabel: 'Return Rate',
    actualLabel: 'Actual',
    minSampleSize: 500,
  },
  penetration: {
    title: 'Deck Penetration',
    description: 'Progress toward the reshuffle point. Higher penetration benefits card counters.',
    formula: 'Cards Dealt ÷ Reshuffle Threshold × 100',
    theMath: 'Reshuffle occurs at 100% (260 cards dealt, ~1 deck remaining). Below 50%, BUSTED! Buddy gives only basic strategy. From 50-100%, index plays are enabled—this is the counting window.',
    expected: '50-100%',
  },
};

function TableStatsSection({ stats }: { stats: TableStats }) {
  return (
    <>
      <StatRowWithInfo
        label="Hands Played"
        value={formatNumber(stats.roundsPlayed)}
        info={TABLE_STAT_INFO.handsPlayed}
        sampleSize={stats.roundsPlayed}
      />
      <StatRowWithInfo
        label="Unique Players"
        value={stats.uniquePlayers}
        info={TABLE_STAT_INFO.uniquePlayers}
      />
      <StatRowWithInfo
        label="Dealer Win Rate"
        value={formatPercent(stats.dealerWinRate)}
        info={TABLE_STAT_INFO.dealerWinRate}
        actual={formatPercent(stats.dealerWinRate)}
        sampleSize={stats.roundsPlayed}
      />
      <StatRowWithInfo
        label="Blackjacks Dealt"
        value={stats.blackjacksDealt}
        info={TABLE_STAT_INFO.blackjacksDealt}
        actual={stats.roundsPlayed > 0 ? formatPercent((stats.blackjacksDealt / stats.roundsPlayed) * 100) : '—'}
        sampleSize={stats.roundsPlayed}
      />
      <StatRowWithInfo
        label="Biggest Win"
        value={formatBucks(stats.biggestWin)}
        info={TABLE_STAT_INFO.biggestWin}
        highlight
      />
      <StatRowWithInfo
        label="Hot Streak"
        value={stats.hotStreak + ' wins'}
        info={TABLE_STAT_INFO.hotStreak}
        actual={stats.hotStreak + ' wins'}
        sampleSize={stats.roundsPlayed}
      />
      <StatRowWithInfo
        label="Total Wagered"
        value={formatBucks(stats.totalWagered)}
        info={TABLE_STAT_INFO.totalWagered}
      />
      <StatRowWithInfo
        label="Total Payouts"
        value={formatBucks(stats.totalPayouts)}
        info={TABLE_STAT_INFO.totalPayouts}
        actual={stats.totalWagered > 0 ? formatPercent((stats.totalPayouts / stats.totalWagered) * 100) + ' return' : '—'}
        sampleSize={stats.roundsPlayed}
      />
      <PenetrationDisplay />
    </>
  );
}

// Stat info definitions for Player stats
const PLAYER_STAT_INFO = {
  handsPlayed: {
    title: 'Hands Played',
    description: 'Total number of hands you have played. Each hand dealt to you counts separately, including split hands.',
    theMath: 'More hands played = more experience and more reliable statistics. Professional players may play thousands of hands per session to achieve meaningful sample sizes.',
  },
  winRate: {
    title: 'Win Rate',
    description: 'Percentage of hands you won (excluding pushes). This reflects your actual results, which combine both skill and variance.',
    formula: 'Hands Won ÷ (Total Hands − Pushes) × 100',
    theMath: 'Expected win rate with perfect basic strategy is approximately 42-43%. Short-term results can vary wildly due to variance. A 35% or 50% win rate over 100 hands is completely normal.',
    expected: '~42%',
    minSampleSize: 500,
  },
  blackjacks: {
    title: 'Blackjacks',
    description: 'Number of natural 21s (Ace + 10-value card) you have been dealt. Blackjacks are the most valuable hands, typically paying 3:2.',
    formula: 'Count of Ace + 10-value on initial deal',
    theMath: 'Probability of receiving a blackjack is 4.83% (about 1 in 21 hands). Blackjacks contribute significantly to your edge—a 6:5 payout instead of 3:2 increases house edge by ~1.4%.',
    expected: '~4.8%',
    minSampleSize: 200,
  },
  bestStreak: {
    title: 'Best Win Streak',
    description: 'Your longest consecutive winning streak. Pushes do not break the streak.',
    theMath: 'With a ~42% win rate, expected longest streak in 100 hands is about 5-6 wins. Streaks are pure variance—a hot streak does not mean you are "due" for losses.',
    expected: '5-6 wins',
    minSampleSize: 100,
  },
  worstStreak: {
    title: 'Worst Loss Streak',
    description: 'Your longest consecutive losing streak. Pushes do not break the streak.',
    theMath: 'With a ~48% loss rate (excluding pushes), losing streaks of 5-7 are common. Long losing streaks feel worse than they are statistically—they are a normal part of the game.',
    expected: '5-7 losses',
    minSampleSize: 100,
  },
  netResult: {
    title: 'Net Result',
    description: 'Your total profit or loss. Positive means you are up; negative means you are down.',
    formula: 'Total Payouts − Total Wagered',
    theMath: 'With a ~0.5% house edge and perfect play, expect to lose about 5 B-Bucks per 1,000 B-Bucks wagered long-term. Short-term swings can be much larger—winning or losing 20+ units in a session is normal.',
    expected: '−0.5% of wagered',
    expectedLabel: 'Long-term',
    actualLabel: 'Current',
    minSampleSize: 1000,
  },
  doublesWon: {
    title: 'Doubles Won',
    description: 'Number of successful double downs. Doubling is one of the most important plays for reducing house edge.',
    theMath: 'You should double about 9-10% of hands with basic strategy. Winning doubles is critical—they occur on your most favorable situations (11 vs dealer 6, for example). Expected win rate on doubles is ~54-58%.',
    expected: '~56% win rate',
    minSampleSize: 50,
  },
  splitsPlayed: {
    title: 'Splits Played',
    description: 'Number of hands where you split a pair. Splitting correctly is essential for optimal play.',
    theMath: 'You should split about 2-3% of hands. Always split Aces and 8s. Never split 10s or 5s. Splitting pairs correctly can reduce house edge by ~0.4%.',
    expected: '~2-3%',
    minSampleSize: 100,
  },
  sidebetsPaid: {
    title: 'Sidebets Paid',
    description: 'Number of sidebets you have won. Sidebets are optional wagers that resolve based on initial cards dealt.',
    theMath: 'Sidebets typically have higher house edges (2-11%) but offer larger payouts. Win rates vary by sidebet type—Perfect Pairs wins ~5% of the time but pays up to 25:1.',
  },
  sidebetWinnings: {
    title: 'Sidebet Winnings',
    description: 'Total gross winnings from sidebets. This is the sum of all sidebet payouts you have received.',
    theMath: 'Sidebet payouts vary by type: Perfect Pairs pays 5:1 to 25:1, 21+3 pays 5:1 to 100:1, Lucky Ladies pays 4:1 to 1000:1. Big wins come from rare combinations.',
  },
};

// Stat info definitions for Sidebet section
const SIDEBET_STAT_INFO = {
  sidebetsPlayed: {
    title: 'Sidebets Played',
    description: 'Total number of sidebets you have placed. Each sidebet on each round counts separately.',
    theMath: 'You can place multiple sidebets per hand. Sidebets resolve independently of your main hand outcome.',
  },
  sidebetsWon: {
    title: 'Sidebets Won',
    description: 'Number of sidebets that paid out. Winning sidebets are determined by your initial cards and/or dealer cards.',
    theMath: 'Win rates vary by sidebet type: Perfect Pairs ~5%, 21+3 ~7%, Lucky Ladies ~2%. Higher payouts compensate for lower win rates.',
  },
  winRate: {
    title: 'Sidebet Win Rate',
    description: 'Percentage of sidebets that paid out. This reflects your actual sidebet results.',
    formula: 'Sidebets Won ÷ Sidebets Played × 100',
    theMath: 'Expected win rates: Perfect Pairs ~5%, 21+3 ~7%, Lucky Ladies ~2%, Blazing 7s ~4%. Short-term variance is high due to rare payouts.',
    expected: '~5-7%',
    minSampleSize: 50,
  },
  totalWinnings: {
    title: 'Total Winnings',
    description: 'Gross winnings from sidebets—the sum of all sidebet payouts you have received.',
    theMath: 'Big sidebet wins come from rare combinations. A Perfect Pair at 25:1 or Suited Trips at 100:1 can produce huge payouts on modest bets.',
  },
  totalWagered: {
    title: 'Total Wagered',
    description: 'Total amount wagered on sidebets. This is your sidebet "handle".',
    theMath: 'Sidebet house edges range from 2-11% depending on type. Despite higher edges, the entertainment value and jackpot potential attract players.',
  },
  biggestWin: {
    title: 'Biggest Sidebet Win',
    description: 'Your largest single sidebet payout. Chase those big multipliers!',
    theMath: 'Maximum payouts: Perfect Pairs 25:1, 21+3 Suited Trips 100:1, Lucky Ladies Q♥Q♥ w/ BJ 1000:1. The bigger the payout, the rarer the hit.',
  },
  netResult: {
    title: 'Sidebet Net Result',
    description: 'Your profit or loss from sidebets. Winnings minus amount wagered.',
    formula: 'Total Winnings − Total Wagered',
    theMath: 'Sidebets have higher house edges (2-11%) than the main game (~0.5%). Long-term expectation is negative, but variance is high—you might hit big!',
    expected: '−2% to −11%',
    expectedLabel: 'House Edge',
    minSampleSize: 100,
  },
};

function PlayerStatsSection({ stats }: { stats: PlayerStats }) {
  return (
    <>
      <StatRowWithInfo
        label="Hands Played"
        value={formatNumber(stats.handsPlayed)}
        info={PLAYER_STAT_INFO.handsPlayed}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Win Rate"
        value={formatPercent(stats.winRate)}
        info={PLAYER_STAT_INFO.winRate}
        actual={formatPercent(stats.winRate)}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Blackjacks"
        value={stats.blackjacks}
        info={PLAYER_STAT_INFO.blackjacks}
        actual={stats.handsPlayed > 0 ? formatPercent((stats.blackjacks / stats.handsPlayed) * 100) : '—'}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Best Streak"
        value={stats.bestStreak + ' wins'}
        info={PLAYER_STAT_INFO.bestStreak}
        actual={stats.bestStreak + ' wins'}
        sampleSize={stats.handsPlayed}
        highlight
      />
      <StatRowWithInfo
        label="Worst Streak"
        value={stats.worstStreak + ' losses'}
        info={PLAYER_STAT_INFO.worstStreak}
        actual={stats.worstStreak + ' losses'}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Net Result"
        value={formatBucks(stats.netResult)}
        info={PLAYER_STAT_INFO.netResult}
        actual={formatBucks(stats.netResult)}
        sampleSize={stats.handsPlayed}
        highlight={stats.netResult > 0}
      />
      <StatRowWithInfo
        label="Doubles Won"
        value={stats.doublesWon}
        info={PLAYER_STAT_INFO.doublesWon}
        actual={stats.doublesWon + ' wins'}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Splits Played"
        value={stats.splitsPlayed}
        actual={stats.handsPlayed > 0 ? formatPercent((stats.splitsPlayed / stats.handsPlayed) * 100) : '—'}
        info={PLAYER_STAT_INFO.splitsPlayed}
        sampleSize={stats.handsPlayed}
      />
      <StatRowWithInfo
        label="Sidebets Paid"
        value={stats.sidebetsPaid}
        info={PLAYER_STAT_INFO.sidebetsPaid}
        highlight={stats.sidebetsPaid > 0}
      />
      <StatRowWithInfo
        label="Sidebet Winnings"
        value={formatBucks(stats.sidebetWinnings)}
        info={PLAYER_STAT_INFO.sidebetWinnings}
        highlight={stats.sidebetWinnings > 0}
      />
    </>
  );
}

// ===========================================
// SIDEBET STATS SECTION
// ===========================================

function SidebetStatsSection({ stats, isGlobal = false }: { stats: SidebetStats; isGlobal?: boolean }) {
  return (
    <>
      <StatRowWithInfo
        label="Sidebets Played"
        value={formatNumber(stats.sidebetsPlayed)}
        info={SIDEBET_STAT_INFO.sidebetsPlayed}
      />
      <StatRowWithInfo
        label="Sidebets Won"
        value={formatNumber(stats.sidebetsWon)}
        info={SIDEBET_STAT_INFO.sidebetsWon}
        highlight={stats.sidebetsWon > 0}
      />
      <StatRowWithInfo
        label="Win Rate"
        value={stats.sidebetsPlayed > 0 ? formatPercent(stats.winRate) : '—'}
        info={SIDEBET_STAT_INFO.winRate}
        actual={stats.sidebetsPlayed > 0 ? formatPercent(stats.winRate) : '—'}
        sampleSize={stats.sidebetsPlayed}
      />
      <StatRowWithInfo
        label={isGlobal ? "Total Payouts" : "Total Winnings"}
        value={formatBucks(stats.totalWinnings)}
        info={SIDEBET_STAT_INFO.totalWinnings}
        highlight={stats.totalWinnings > 0}
      />
      <StatRowWithInfo
        label="Total Wagered"
        value={formatBucks(stats.totalWagered)}
        info={SIDEBET_STAT_INFO.totalWagered}
      />
      <StatRowWithInfo
        label="Biggest Win"
        value={formatBucks(stats.biggestWin)}
        info={SIDEBET_STAT_INFO.biggestWin}
        highlight={stats.biggestWin > 0}
      />
      {!isGlobal && (
        <StatRowWithInfo
          label="Net Result"
          value={formatBucks(stats.netResult)}
          info={SIDEBET_STAT_INFO.netResult}
          actual={formatBucks(stats.netResult)}
          sampleSize={stats.sidebetsPlayed}
          highlight={stats.netResult > 0}
        />
      )}
    </>
  );
}

// Stat info definitions for Global stats
const GLOBAL_STAT_INFO = {
  playersOnline: {
    title: 'Players Online',
    description: 'Number of players currently connected to the casino. This shows real-time activity across all tables.',
    theMath: 'More players = more action and social interaction. Popular times typically see higher player counts.',
  },
  uniquePlayers: {
    title: 'Unique Players',
    description: 'Total number of distinct players who have visited the casino. Each player is counted once regardless of sessions.',
  },
  tablesActive: {
    title: 'Tables Active',
    description: 'Number of tables currently in use with active players. Tables become active when a player joins.',
  },
  uniqueTables: {
    title: 'Unique Tables',
    description: 'Total number of distinct tables that have been used. Includes all tables ever created.',
  },
  handsPlayedGlobal: {
    title: 'Hands Played',
    description: 'Total hands dealt across all tables and all players. This is a measure of overall casino activity.',
    theMath: 'A busy blackjack table deals about 60-80 hands per hour. This number grows with player activity across the platform.',
  },
  chipsWagered: {
    title: 'Chips Wagered',
    description: 'Total amount of chips bet across all tables. Also known as "handle" in casino terminology.',
    theMath: 'The theoretical house profit is approximately 0.5% of total chips wagered with perfect basic strategy, or 2-4% with average play.',
  },
  biggestWinGlobal: {
    title: 'Biggest Win',
    description: 'The largest single-hand win across all players and tables. This is the casino record for a single hand.',
    theMath: 'Big wins typically come from doubled hands, blackjacks on max bets, or winning multiple split hands.',
  },
  blackjacksGlobal: {
    title: 'Blackjacks',
    description: 'Total natural 21s dealt to all players across the casino.',
    theMath: 'With a 4.83% probability, expect roughly 1 blackjack per 21 hands dealt. This counter tracks the total across all players.',
    expected: '~4.8%',
    minSampleSize: 500,
  },
  luckiestPlayer: {
    title: 'Luckiest Player',
    description: 'The player with the highest single-hand win or the best current session. Bragging rights for the hottest player!',
  },
};

function GlobalStatsSection({ stats, isLifetime = false }: { stats: GlobalStats; isLifetime?: boolean }) {
  return (
    <>
      <StatRowWithInfo
        label={isLifetime ? "Unique Players" : "Players Online"}
        value={formatNumber(stats.playersOnline)}
        info={isLifetime ? GLOBAL_STAT_INFO.uniquePlayers : GLOBAL_STAT_INFO.playersOnline}
      />
      <StatRowWithInfo
        label={isLifetime ? "Unique Tables" : "Tables Active"}
        value={stats.tablesActive}
        info={isLifetime ? GLOBAL_STAT_INFO.uniqueTables : GLOBAL_STAT_INFO.tablesActive}
      />
      <StatRowWithInfo
        label="Hands Played"
        value={formatNumber(stats.handsPlayed)}
        info={GLOBAL_STAT_INFO.handsPlayedGlobal}
      />
      <StatRowWithInfo
        label="Chips Wagered"
        value={formatBucks(stats.chipsWagered)}
        info={GLOBAL_STAT_INFO.chipsWagered}
      />
      <StatRowWithInfo
        label="Biggest Win"
        value={formatBucks(stats.biggestWin)}
        info={GLOBAL_STAT_INFO.biggestWinGlobal}
        highlight
      />
      <StatRowWithInfo
        label="Blackjacks"
        value={formatNumber(stats.blackjacks)}
        info={GLOBAL_STAT_INFO.blackjacksGlobal}
        actual={stats.handsPlayed > 0 ? formatPercent((stats.blackjacks / stats.handsPlayed) * 100) : '—'}
        sampleSize={stats.handsPlayed}
      />
      {stats.luckiestPlayer && (
        <StatRowWithInfo
          label="Luckiest Player"
          value={stats.luckiestPlayer}
          info={GLOBAL_STAT_INFO.luckiestPlayer}
          highlight
        />
      )}
    </>
  );
}

// ===========================================
// BUSTED! BUDDY STATS
// ===========================================

interface BuddyStats {
  handsAnalyzed: number;
  adherenceRate: number;
  winRateWithBuddy: number;
  winRateWithoutBuddy: number;
  trueCountDeviations: number;
  deviationSuccessRate: number;
  insuranceAccuracy: number;
  insuranceOffered: number;
  bestStreakWithBuddy: number;
  bestStreakWithoutBuddy: number;
}

// Stat info definitions for Buddy stats
const BUDDY_STAT_INFO = {
  basicStrategy: {
    title: 'Basic Strategy Compliance',
    description: 'Percentage of hands played according to optimal basic strategy. Basic strategy is the mathematically optimal play for every possible hand combination, calculated from millions of simulated hands.',
    formula: 'Decisions Following Buddy ÷ Total Decisions × 100',
    theMath: 'Perfect basic strategy reduces the house edge to approximately 0.5%. Every deviation from basic strategy increases the house edge, typically by 0.1-2% depending on the error.',
    expected: '100%',
    minSampleSize: 50,
  },
  decisionsTracked: {
    title: 'Decisions Tracked',
    description: 'Total number of player decisions analyzed by Buddy during this session. Each decision point (hit, stand, double, split, surrender, insurance) is tracked.',
    formula: 'Count of all recorded decisions',
    theMath: 'More decisions = more reliable statistics. Law of large numbers means your actual results converge to expected values over many hands.',
    minSampleSize: 100,
  },
  indexDeviations: {
    title: 'Index Play Deviations',
    description: 'Departures from basic strategy based on the Hi-Lo true count. These "index plays" exploit favorable deck compositions where the remaining cards significantly change the optimal play.',
    formula: 'Count of plays where true count justified deviation',
    theMath: 'Index plays are triggered at specific true counts (e.g., "Stand on 16 vs 10 at TC ≥ 0"). Each index play has a calculated threshold where deviating from basic strategy becomes +EV.',
    minSampleSize: 200,
  },
  deviationWinRate: {
    title: 'Deviation Win Rate',
    description: 'Percentage of index play deviations that resulted in a winning hand. While deviations are mathematically +EV, short-term results are highly variable.',
    formula: 'Deviation Wins ÷ Total Deviations × 100',
    theMath: 'Even perfect index plays only provide a small edge (~0.1-0.5% per deviation). With such thin margins, expect roughly 50% win rate with high variance. The edge comes from cumulative +EV over thousands of hands.',
    expected: '~50%',
    minSampleSize: 100,
  },
  insuranceEfficiency: {
    title: 'Insurance Efficiency',
    description: 'Accuracy of insurance decisions based on the true count. Buddy recommends insurance only when the true count is +3 or higher, when the probability of dealer blackjack exceeds the break-even point.',
    formula: 'Correct Insurance Decisions ÷ Total Insurance Offers × 100',
    theMath: 'Insurance pays 2:1 and breaks even when dealer has blackjack >33.3% of the time. At TC ≥ +3, the deck is rich enough in tens that dealer blackjack probability exceeds this threshold, making insurance +EV.',
    expected: '~68%',
    minSampleSize: 30,
  },
  houseEdge: {
    title: 'Estimated House Edge',
    description: 'The casino\'s mathematical advantage against you. Lower is better for the player. This estimate reflects your adherence to basic strategy.',
    // FIX 5: Removed fabricated "Counting Benefit" term - formula now only considers adherence
    formula: 'Base edge 0.5% + penalty for deviating from basic strategy',
    theMath: 'Without any strategy, house edge is ~2-4%. Perfect basic strategy reduces it to ~0.5%. Each deviation from basic strategy increases your edge slightly. Following Buddy\'s advice keeps you at optimal play.',
    expected: '0.5%',
    expectedLabel: 'Basic',
    actualLabel: 'With Buddy',
    minSampleSize: 500,
    lowerIsBetter: true,
  },
};

function BuddyStatsSection({ stats }: { stats: BuddyStats }) {
  // FIX 5: Calculate estimated house edge based on play quality
  // Base: 0.5% for perfect basic strategy
  // Add penalty for deviating from basic strategy (up to 2%)
  // Removed: countingBenefit term was fabricated (granted 0.3% for ANY deviation, correct or not)
  const baseEdge = 0.5;
  const adherencePenalty = ((100 - stats.adherenceRate) / 100) * 2; // Up to 2% penalty for ignoring advice
  const estimatedEdge = Math.max(0, baseEdge + adherencePenalty);

  return (
    <>
      {/* Strategy Compliance */}
      <div className="pb-1">
        <div className="text-white/40 uppercase tracking-wide mb-1 opacity-80">Strategy Compliance</div>
      </div>
      <StatRowWithInfo
        label="Basic Strategy"
        value={formatPercent(stats.adherenceRate)}
        info={BUDDY_STAT_INFO.basicStrategy}
        actual={formatPercent(stats.adherenceRate)}
        sampleSize={stats.handsAnalyzed}
        highlight={stats.adherenceRate === 100}
      />
      <StatRowWithInfo
        label="Decisions Tracked"
        value={formatNumber(stats.handsAnalyzed)}
        info={BUDDY_STAT_INFO.decisionsTracked}
        sampleSize={stats.handsAnalyzed}
      />

      {/* Advantage Play */}
      <div className="pt-2 pb-1">
        <div className="text-white/40 uppercase tracking-wide mb-1 opacity-80">Advantage Play</div>
      </div>
      <StatRowWithInfo
        label="Index Deviations"
        value={formatNumber(stats.trueCountDeviations)}
        info={BUDDY_STAT_INFO.indexDeviations}
        sampleSize={stats.handsAnalyzed}
      />
      <StatRowWithInfo
        label="Deviation Win Rate"
        value={stats.trueCountDeviations > 0 ? formatPercent(stats.deviationSuccessRate) : '—'}
        info={BUDDY_STAT_INFO.deviationWinRate}
        actual={stats.trueCountDeviations > 0 ? formatPercent(stats.deviationSuccessRate) : '—'}
        sampleSize={stats.trueCountDeviations}
      />
      <StatRowWithInfo
        label="Insurance Efficiency"
        value={stats.insuranceOffered >= 5 ? formatPercent(stats.insuranceAccuracy) : '—'}
        info={BUDDY_STAT_INFO.insuranceEfficiency}
        actual={stats.insuranceOffered >= 5 ? formatPercent(stats.insuranceAccuracy) : '—'}
        sampleSize={stats.insuranceOffered}
      />

      {/* Your Edge */}
      <div className="pt-2 pb-1">
        <div className="text-white/40 uppercase tracking-wide mb-1 opacity-80">Your Edge</div>
      </div>
      <StatRowWithInfo
        label="Est. House Edge"
        value={estimatedEdge.toFixed(1) + '%'}
        info={BUDDY_STAT_INFO.houseEdge}
        actual={estimatedEdge.toFixed(1) + '%'}
        sampleSize={stats.handsAnalyzed}
        highlight={estimatedEdge < 0.5}
      />

      {stats.handsAnalyzed < 10 && stats.handsAnalyzed > 0 && (
        <div className="pt-2 text-white/40 italic text-center opacity-80">
          Play {10 - stats.handsAnalyzed} more hands for full stats
        </div>
      )}
    </>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export function StatsPanel({ stats, fontClass = 'text-base' }: StatsPanelProps) {
  const showRecommendations = useAuthStore((state) => state.user?.showRecommendations ?? false);
  const tableId = useGameStateStore((state) => state.tableId);
  const [buddyStats, setBuddyStats] = useState<BuddyStats | null>(null);
  const [buddyStatsLoading, setBuddyStatsLoading] = useState(false);
  const hasLoadedBuddyStats = useRef(false);

  // Fetch Buddy stats when recommendations are enabled or when main stats refresh
  // Using stats as a dependency ensures Buddy stats refresh in real-time alongside other stats
  // Stats are now per-table, so tableId is required
  useEffect(() => {
    if (!showRecommendations || !tableId) {
      setBuddyStats(null);
      hasLoadedBuddyStats.current = false;
      return;
    }

    // Only show loading on initial fetch, not on refreshes (prevents flashing)
    if (!hasLoadedBuddyStats.current) {
      setBuddyStatsLoading(true);
    }

    api.nbaAudit.getBuddyStats(tableId)
      .then((response) => {
        if (response.success && response.data) {
          setBuddyStats(response.data.stats);
          hasLoadedBuddyStats.current = true;
        }
      })
      .catch((err) => {
        console.error('Failed to fetch Buddy stats:', err);
      })
      .finally(() => {
        setBuddyStatsLoading(false);
      });
  }, [showRecommendations, tableId, stats]);

  if (!stats) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-white/40">Loading stats...</div>
      </div>
    );
  }

  // Fallback for myTableStats if not present (backwards compatibility)
  const myTableStats = stats.myTableStats ?? {
    handsPlayed: 0,
    winRate: 0,
    blackjacks: 0,
    bestStreak: 0,
    worstStreak: 0,
    netResult: 0,
    doublesWon: 0,
    splitsPlayed: 0,
    sidebetsPaid: 0,
    sidebetWinnings: 0,
    sidebetWagered: 0,
    biggestSidebetWin: 0,
  };

  // Fallback for sidebet stats
  const emptySidebetStats: SidebetStats = {
    sidebetsPlayed: 0,
    sidebetsWon: 0,
    winRate: 0,
    totalWinnings: 0,
    totalWagered: 0,
    biggestWin: 0,
    netResult: 0,
  };
  const mySidebetStats = stats.mySidebetStats ?? emptySidebetStats;
  const sidebetToday = stats.sidebetToday ?? emptySidebetStats;
  const sidebetLifetime = stats.sidebetLifetime ?? emptySidebetStats;
  const globalSidebetToday = stats.globalSidebetToday ?? emptySidebetStats;
  const globalSidebetLifetime = stats.globalSidebetLifetime ?? emptySidebetStats;

  return (
    <div className={`flex-1 overflow-y-auto ${fontClass}`}>
      <CollapsibleSection title={`My Table Stats${tableId ? ` (${tableId})` : ''}`} defaultOpen={true}>
        <PlayerStatsSection stats={myTableStats} />
      </CollapsibleSection>

      <CollapsibleSection title="Full Table Stats" defaultOpen={false}>
        <TableStatsSection stats={stats.table} />
      </CollapsibleSection>

      <CollapsibleSection title="Player Today" defaultOpen={false}>
        <PlayerStatsSection stats={stats.playerToday} />
      </CollapsibleSection>

      <CollapsibleSection title="Player Lifetime" defaultOpen={false}>
        <PlayerStatsSection stats={stats.playerLifetime} />
      </CollapsibleSection>

      <CollapsibleSection title="Global Today" defaultOpen={false}>
        <GlobalStatsSection stats={stats.globalToday} />
      </CollapsibleSection>

      <CollapsibleSection title="Global Lifetime" defaultOpen={false}>
        <GlobalStatsSection stats={stats.globalLifetime} isLifetime />
      </CollapsibleSection>

      {/* My Sidebet Wins - Combined section with subsections */}
      <CollapsibleSection
        title="My Sidebet Wins"
        defaultOpen={false}
        titleClassName="text-amber-400"
      >
        <div className="space-y-3">
          {/* This Table subsection */}
          <div>
            <div className="text-xs text-amber-400/70 uppercase tracking-wide mb-1 font-semibold">This Table</div>
            <div className="pl-2 border-l border-amber-400/20 space-y-1">
              <SidebetStatsSection stats={mySidebetStats} />
            </div>
          </div>

          {/* Today subsection */}
          <div>
            <div className="text-xs text-amber-400/70 uppercase tracking-wide mb-1 font-semibold">Today</div>
            <div className="pl-2 border-l border-amber-400/20 space-y-1">
              <SidebetStatsSection stats={sidebetToday} />
            </div>
          </div>

          {/* Lifetime subsection */}
          <div>
            <div className="text-xs text-amber-400/70 uppercase tracking-wide mb-1 font-semibold">Lifetime</div>
            <div className="pl-2 border-l border-amber-400/20 space-y-1">
              <SidebetStatsSection stats={sidebetLifetime} />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Global Sidebet Wins - Combined section with subsections */}
      <CollapsibleSection
        title="Global Sidebet Wins"
        defaultOpen={false}
        titleClassName="text-amber-400"
      >
        <div className="space-y-3">
          {/* Today subsection */}
          <div>
            <div className="text-xs text-amber-400/70 uppercase tracking-wide mb-1 font-semibold">Today</div>
            <div className="pl-2 border-l border-amber-400/20 space-y-1">
              <SidebetStatsSection stats={globalSidebetToday} isGlobal />
            </div>
          </div>

          {/* Lifetime subsection */}
          <div>
            <div className="text-xs text-amber-400/70 uppercase tracking-wide mb-1 font-semibold">Lifetime</div>
            <div className="pl-2 border-l border-amber-400/20 space-y-1">
              <SidebetStatsSection stats={globalSidebetLifetime} isGlobal />
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* BUSTED! Buddy Stats - Only shown when Buddy is enabled */}
      {showRecommendations && tableId && (
        <CollapsibleSection
          title={`BUSTED! Buddy (Table: ${tableId})`}
          defaultOpen={false}
          titleClassName="text-yellow-400"
        >
          {buddyStatsLoading ? (
            <div className="text-white/40 text-center py-2">Loading...</div>
          ) : buddyStats ? (
            <BuddyStatsSection stats={buddyStats} />
          ) : (
            <div className="text-white/40 text-center py-2">No Buddy data yet</div>
          )}
        </CollapsibleSection>
      )}
    </div>
  );
}

export default StatsPanel;
